import { Injectable } from '@angular/core'
import { ExpenseClaimDetail, ExpenseClaim } from '../../model/expense_claim.model'
import { ClassValidatorFormBuilderService, ClassValidatorFormGroup } from 'ngx-reactive-form-class-validator'
import { UntypedFormControl, Validators } from '@angular/forms'
import { ObjectMapper } from '../../core/object_mapper.service'
import { debounceTime } from 'rxjs'

export type MetaData = {
    'ExpenseClaim': MetaDataObject<'schema', 'form'>,
    'ExpenseClaimDetail': MetaDataObject<'schema', 'form'>
}

type MetaDataObject<T, K> = {
    'schema': ExpenseClaim | ExpenseClaimDetail,
    'form'?: ClassValidatorFormGroup
}

// Responsible to preparing and validating the metadata for eclaim
@Injectable({
    providedIn: 'root'
})
export class EclaimMetaData {
    constructor(
        private form: ClassValidatorFormBuilderService,
        private db: ObjectMapper
    ) { }

    initMetaData(): MetaData {
        // Prepare required metadata for parent and child table
        let expense_claim: ExpenseClaim = this.db.getdocschema('ExpenseClaim')
        let expense_claim_details: ExpenseClaimDetail = this.db.getdocschema('ExpenseClaimDetail')
        return {
            'ExpenseClaim': {
                'schema': expense_claim,
            },
            'ExpenseClaimDetail': {
                'schema': expense_claim_details,
                'form': this.intiExpenseClaimDetailForm()
            }
        }
    }

    initMetaDataEdit(expenseDetail: ExpenseClaimDetail, claimInfo: string): MetaData {
        // Prepare required metadata for parent and child table
        // console.log(expenseDetail)
        let expense_claim: ExpenseClaim = this.db.getdocschema('ExpenseClaim')
        let expense_claim_details: ExpenseClaimDetail = this.db.getdocschema('ExpenseClaimDetail')
        return {
            'ExpenseClaim': {
                'schema': expense_claim,
            },
            'ExpenseClaimDetail': {
                'schema': expense_claim_details,
                'form': this.initEditExpenseClaimDetail(expenseDetail, claimInfo)
            }
        }
    }

    initEditExpenseClaimDetail(data: ExpenseClaimDetail, claimInfo: string): ClassValidatorFormGroup {
        // console.log({ data }, 'metadata ..... metadata');
        let temp = {
            name: [data.name],
            expense_date: [data.expense_date],
            expense_type: [data.expense_type],
            vehicle_type: [data.vehicle_type],
            invoice_number: [data.invoice_number],
            amount: [data.amount],
            description: [data.description],
            expense_date_from: [data.expense_date_from],
            expense_date_to: [data.expense_date_to],
            meal_types: [data.meal_types],
            vehicle_number_plate: [data.vehicle_number_plate],
            claim_on_behalf: [data.claim_on_behalf],
            travelling_purpose: [data.travelling_purpose],
            transportation: [data.transportation],
            region: [data.region],
            file_attachment: [data.file_attachment],
            claim_info: claimInfo != null ? [claimInfo] : '',
            membership_validity: [data.membership_validity],
            membership_purpose: [data.membership_purpose],
            membership_validity_from: [data.membership_validity_from],
            membership_validity_to: [data.membership_validity_to]
        }

        if (data.mileage_to != undefined || data.mileage_to != null) {
            temp['mileage_to'] = [data.mileage_to]
            temp['mileage_from'] = [data.mileage_from]
            temp['mileage_return'] = [data.mileage_return]
            temp['mileage_distance'] = [data.mileage_distance]
            temp['transport_allowance'] = [data.disclaimed_transport_allowance]
            temp['deducted_transport_allowance'] = [data.deducted_transport_allowance]
            temp['mileage_description'] = [data.mileage_description]
        }
        return this.form.group(ExpenseClaimDetail, temp)
    }

    intiExpenseClaimDetailForm(): ClassValidatorFormGroup {
        return this.form.group(ExpenseClaimDetail, {
            name: [''],
            expense_date: [''],
            expense_type: [''],
            invoice_number: [''],
            amount: [''],
            description: [''],
            file_attachment: [''],
            claim_info: ['']
        })
    }

    // Set the listener on form page
    activateMilleageClaim(form: ClassValidatorFormGroup) {
        // console.log({ form });

        // Activate mileage claim
        if (form.contains('expense_type')) {
            // Fetch expense type
            if (form.controls['expense_type'].value['name'] == 'MILEAGE') {

                // Avoid replacing if already mapped
                if (form.contains('mileage_to') || form.contains('mileage_from')) {
                    return
                }

                form.addControl('vehicle_type', new UntypedFormControl('', Validators.required), { emitEvent: false })
                form.addControl('mileage_to', new UntypedFormControl('', Validators.required), { emitEvent: false })
                form.addControl('mileage_from', new UntypedFormControl('', Validators.required), { emitEvent: false })
                form.addControl('mileage_return', new UntypedFormControl(''), { emitEvent: false })
                form.addControl('mileage_distance', new UntypedFormControl(''), { emitEvent: false })
                form.addControl('transport_allowance', new UntypedFormControl(''), { emitEvent: false })
                form.addControl('deducted_transport_allowance', new UntypedFormControl(''), { emitEvent: false })
                form.addControl('mileage_description', new UntypedFormControl(''), { emitEvent: false })


                form.get('vehicle_type')
                form.get('mileage_to')
                form.get('mileage_from')
                form.get('mileage_return')
                form.get('mileage_distance')
                form.get('transport_allowance')
                form.get('deducted_transport_allowance')
                form.get('mileage_description')
                form.updateValueAndValidity({ emitEvent: false })

            } else {
                if (form.contains('mileage_to') || form.contains('mileage_from')) {
                    form.removeControl('vehicle_type', { emitEvent: false })
                    form.removeControl('mileage_to', { emitEvent: false })
                    form.removeControl('mileage_from', { emitEvent: false })
                    form.removeControl('mileage_return', { emitEvent: false })
                    form.removeControl('mileage_distance', { emitEvent: false })
                    form.removeControl('transport_allowance', { emitEvent: false })
                    form.removeControl('deducted_transport_allowance', { emitEvent: false })
                    form.removeControl('mileage_description', { emitEvent: false })

                    form.clearValidators()
                    form.updateValueAndValidity({ emitEvent: false })
                }
            }

            if (form.controls['expense_type'].value['date_range'] === 1) {
                form.addControl('expense_date_from', new UntypedFormControl('', Validators.required), { emitEvent: false })
                form.addControl('expense_date_to', new UntypedFormControl('', Validators.required), { emitEvent: false })


                form.get('expense_date_from')
                form.get('expense_date_to')

                const today = new Date().setHours(0, 0, 0, 0); // Today's date without time

                form.get('expense_date_from')?.setValidators([
                    Validators.required,
                    (control) => {
                        const fromDate = control.value;
                        const toDate = form.get('expense_date_to')?.value;
                        // const today = new Date().setHours(0, 0, 0, 0); // Today's date without time

                        if (!fromDate) {
                            return { required: true }; // Required validation
                        }

                        const fromDateObj = new Date(fromDate).setHours(0, 0, 0, 0);

                        if (fromDateObj > today) {
                            control.markAsTouched();
                            return { invalidFromDate: true }; // 🚨 "Expense From Date cannot be after today's date!"
                        }

                        if (toDate) {
                            const toDateObj = new Date(toDate).setHours(0, 0, 0, 0);
                            if (fromDateObj > toDateObj) {
                                control.markAsTouched();
                                return { fromDateAfterToDate: true }; // 🚨 "Expense From Date cannot be after Expense To Date!"
                            }
                        }

                        return null;
                    }
                ]);



                // 🚨 Custom Validator for `expense_date_to`
                form.get('expense_date_to')?.setValidators([
                    Validators.required,
                    (control) => {
                        const fromDate = form.get('expense_date_from')?.value;
                        const toDate = control.value;
                        // const today = new Date().setHours(0, 0, 0, 0); // Today's date without time

                        if (!toDate) {
                            return { required: true }; // Required validation
                        }

                        const toDateObj = new Date(toDate).setHours(0, 0, 0, 0);
                        const fromDateObj = fromDate ? new Date(fromDate).setHours(0, 0, 0, 0) : null;

                        if (fromDateObj && toDateObj < fromDateObj) {
                            control.markAsTouched();
                            return { invalidDateRange: true }; // 🚨 "Expense To Date cannot be earlier than Expense From Date!"
                        }

                        // if (toDateObj > today) {
                        //     control.markAsTouched();
                        //     return { futureOrTodayDateNotAllowed: true }; // 🚨 "Expense To Date cannot be today or in the future!"
                        // }

                        // ✅ If valid, clear error from From Date
                        form.get('expense_date_from')?.setErrors(null);

                        return null;
                    }
                ]);


                // 🔄 Update Validity
                form.get('expense_date_from')?.valueChanges.subscribe(() => {
                    form.get('expense_date_to')?.updateValueAndValidity({ emitEvent: false });
                });

                form.get('expense_date_to')?.valueChanges.subscribe(() => {
                    form.get('expense_date_from')?.updateValueAndValidity({ emitEvent: false });
                });
            } else {
                form.removeControl('expense_date_from', { emitEvent: false })
                form.removeControl('expense_date_to', { emitEvent: false })

                form.updateValueAndValidity({ emitEvent: false })
            }

            if (form.controls['expense_type'].value['attachment_required'] === 1) {
                form.setControl('file_attachment', new UntypedFormControl('', Validators.required));
            } else {
                form.setControl('file_attachment', new UntypedFormControl(''));
                form.controls['file_attachment'].clearValidators();
                form.controls['file_attachment'].updateValueAndValidity();
                // Reset the control to remove the 'invalid' state
            }

            if (form.controls['expense_type'].value['name'] === 'MEAL ALLOWANCE') {
                form.addControl('meal_types', new UntypedFormControl(''), { emitEvent: false })
                form.get('meal_types')
            } else {
                form.removeControl('meal_types', { emitEvent: false })
            }

            if (form.controls['expense_type'].value['expense_sharing'] === 1) {
                form.addControl('claim_on_behalf', new UntypedFormControl(''), { emitEvent: false })
                form.get('claim_on_behalf')
            } else {
                form.removeControl('claim_on_behalf', { emitEvent: false })
            }

            if (form.controls['expense_type'].value['region_allocation'] === 1) {
                form.addControl('region', new UntypedFormControl('Peninsular', Validators.required), { emitEvent: false })
                form.get('region')
            } else {
                form.removeControl('region', { emitEvent: false })
            }

            if (form.controls['expense_type'].value['name'] === 'PETROL (BILL)') {
                form.addControl('vehicle_number_plate', new UntypedFormControl('', Validators.required), { emitEvent: false })
            } else {
                form.removeControl('vehicle_number_plate', { emitEvent: false })
            }

            if (form.controls['expense_type'].value['name'] === 'PUBLIC TRANSPORT') {
                form.addControl('travelling_purpose', new UntypedFormControl('', Validators.required), { emitEvent: false })
                form.addControl('transportation', new UntypedFormControl('', Validators.required), { emitEvent: false })
            } else {
                form.removeControl('travelling_purpose', { emitEvent: false })
                form.removeControl('transportation', { emitEvent: false })
            }

            if (form.controls['expense_type'].value['name'] === 'PROFESSIONAL MEMBERSHIP REGISTRATION OR RENEWAL') {
                form.addControl('membership_validity_from', new UntypedFormControl(''), { emitEvent: false })
                form.addControl('membership_validity_to', new UntypedFormControl(''), { emitEvent: false })
                form.addControl('membership_purpose', new UntypedFormControl('', Validators.required), { emitEvent: false })
                form.addControl('membership_validity', new UntypedFormControl('Registration', Validators.required), { emitEvent: false })

                // 🚨 Custom Validator for `expense_date_to`
                form.get('membership_validity_to')?.setValidators([
                    (control) => {
                        const fromDate = form.get('membership_validity_from')?.value;
                        const toDate = control.value;

                        const toDateObj = new Date(toDate).setHours(0, 0, 0, 0);
                        const fromDateObj = fromDate ? new Date(fromDate).setHours(0, 0, 0, 0) : null;

                        if (fromDateObj && toDateObj < fromDateObj) {
                            control.markAsTouched();
                            return { invalidDateRange: true }; // 🚨 "Expense To Date cannot be earlier than Expense From Date!"
                        }

                        form.get('membership_validity_from')?.setErrors(null);

                        return null;
                    }
                ]);


            } else {
                form.removeControl('membership_validity', { emitEvent: false })
                form.removeControl('membership_purpose', { emitEvent: false })
                form.removeControl('membership_validity_from', { emitEvent: false })
                form.removeControl('membership_validity_to', { emitEvent: false })
            }

        }
    }

    attachementModeFormControls(expenseType: any, form: ClassValidatorFormGroup) {
        if (expenseType['attachment_required'] === 1) {
            // form.controls['file_attachment'].setValue('', { emitEvent: false });
            form.setControl('file_attachment', new UntypedFormControl('', Validators.required));
        } else {
            // form.controls['file_attachment'].setValue('', { emitEvent: false });
            form.setControl('file_attachment', new UntypedFormControl(''));
            form.controls['file_attachment'].clearValidators();
            form.controls['file_attachment'].updateValueAndValidity();
        }

        if (form.controls['mileage_to'] || form.controls['mileage_from']) {
            if (form.controls['mileage_to'].value == 'Others' || form.controls['mileage_from'].value == 'Others') {
                form.setControl('file_attachment', new UntypedFormControl('', Validators.required));
            } else {
                form.setControl('file_attachment', new UntypedFormControl(''));
                form.controls['file_attachment'].clearValidators();
                form.controls['file_attachment'].updateValueAndValidity();
            }
        }

    }

    // checkAmountControls(form: ClassValidatorFormGroup) {
    //     if (form.contains('amount')) {
    //         form.removeControl('amount', { emitEvent: false })
    //         form.addControl('amount', new UntypedFormControl('', Validators.required), { emitEvent: false })
    //     }
    // }


}